// Benign network-cancellation noise from Turbo fetches (navigation away,
// offline, ad-blockers). The request never reached the server, so it's not
// an app bug — real server errors surface via backend monitoring. Each
// browser words the same failure differently:
//   Chrome:  "TypeError: Failed to fetch"
//   Firefox: "TypeError: NetworkError when attempting to fetch resource."
//   Safari:  "TypeError: Load failed"
// Honeybadger prefixes unhandled rejections ("UnhandledPromiseRejectionWarning:
// TypeError: TypeError: Load failed"), so the Safari match is anchored to the
// end of the message rather than to its start.
const FAILED_TO_FETCH = /failed to fetch|NetworkError when attempting to fetch resource|\bTypeError: Load failed\.?$/i;

// In-app browsers (Facebook, Instagram, Messenger) inject their own scripts
// into every page under an `iabjs://` origin. Failures of their native bridge
// (e.g. "Error invoking postMessage: Java exception was raised during method
// invocation" on beforeunload) bubble up to window.onerror but have nothing
// to do with our code.
const IN_APP_BROWSER_SCRIPT = /\biabjs:\/\//;
const IN_APP_BROWSER_BRIDGE_ERROR = /Error invoking postMessage/i;

// Browser extensions inject content scripts into every page; their failures
// (e.g. an Opera user's extension rejecting a promise over 'M_ID') reach
// window.onunhandledrejection but never touch our bundle. Chromium-family
// browsers (incl. Opera and Edge) use chrome-extension://, Firefox uses
// moz-extension://, Safari uses safari-(web-)extension://.
const BROWSER_EXTENSION_SCRIPT = /\b(?:chrome|moz|safari|safari-web)-extension:\/\//;

// Benign-by-spec browser warning surfaced at window.onerror: a ResizeObserver
// callback changed layout, so the remaining notifications are simply delivered
// next frame. Nothing failed and no exception was thrown — but third-party
// libraries (Fancybox, Chart.js) and older cached bundles can still trigger
// it. The legacy wording is what Chrome < 88 used.
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/;

// An AbortError is an intentional cancellation, not a failure. We never call
// AbortController ourselves; these come from Turbo 8's link prefetch, which
// aborts its in-flight fetch when the pointer leaves the link or a real
// navigation commits — the rejection then escapes unhandled. Matched by name
// when the notice carries one, plus per-browser wordings behind the
// "AbortError:" prefix:
//   Chrome:  "The user aborted a request."
//   Firefox: "The operation was aborted."
//   Safari:  "Fetch is aborted"
const ABORTED_FETCH = /\bAbortError: (?:The user aborted a request|The operation was aborted|Fetch is aborted|signal is aborted)/;

// "Failed to create a WebSocket: the provided URL is invalid." is an old
// Blink check on a null URL string, unreachable from script in current
// Chromium. It came from a client reporting Chrome 149 on /configurator, and
// the cable URL logged with it was a valid wss://superfotky.sk/cable: an old
// engine behind a modern user agent (crawlers hit /configurator a lot), not
// our URL. A genuinely bad URL reads "The URL '...' is invalid." in Chrome and
// stays reported. turbo-cable-stream-source already catches the failure.
const OLD_BLINK_WEBSOCKET_NULL_URL = /Failed to create a WebSocket: the provided URL is invalid/;

export function ignorableNotice(notice) {
  if (!notice) return false;

  const message = notice.message || "";
  const stack = notice.stack || "";

  if (notice.name === "AbortError") return true;

  return FAILED_TO_FETCH.test(message) ||
    IN_APP_BROWSER_SCRIPT.test(stack) ||
    IN_APP_BROWSER_BRIDGE_ERROR.test(message) ||
    BROWSER_EXTENSION_SCRIPT.test(stack) ||
    RESIZE_OBSERVER_LOOP.test(message) ||
    ABORTED_FETCH.test(message) ||
    OLD_BLINK_WEBSOCKET_NULL_URL.test(message);
}
